import "../powerdash.css";

import { ANALYTICS_EVENTS, MAP_TILE_LAYERS } from "../constants";
import { Col, Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";

import { BottomBar } from "./BottomBar";
import { MapModeSelector } from "./MapModeSelector/MapModeSelector";
import PropTypes from "prop-types";
import { SiteDrawer } from "./SiteDrawer/SiteDrawer";
import { WorldMap } from "./WorldMap";
import { WorldMapHud } from "./WorldMapHud";
import { fetchResultMapFeatures } from "../actions/resultMap";
import { fetchUserTrials } from "../actions/user";
import { useHistory } from "react-router-dom";
import { useTracking } from "../analytics";

export const WorldMapLayout = ({ match }) => {
  const {
    trials: userTrials,
    contractsLoading,
    trialsLoading,
  } = useSelector(({ user }) => user);
  const [trial, trialDate, refreshing, mapMode] = useSelector(
    ({ resultMap }) => [
      resultMap.trial,
      resultMap.trial_date,
      resultMap.refreshing,
      resultMap.mapMode,
    ]
  );

  // Creating this ref to access leaflet map in siblings and hooks
  // because putting custom components within MapContainer creates styling and behaviour issues
  const mapRef = useRef();

  const [selectedTileLayer, setSelectedTileLayer] = useState(
    MAP_TILE_LAYERS.SATELLITE
  );

  // Advanced filtering
  const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
  const { trackEvent } = useTracking();

  const toggleOffcanvas = () => {
    setIsOffcanvasOpen(!isOffcanvasOpen);
    trackEvent(ANALYTICS_EVENTS.MAP_AF_BUTTON);
  };

  // Fit zoom to site and reset ortho on trial change only and on remount
  const [prevTrial, setPrevTrial] = useState(null);
  if (trial !== prevTrial) setPrevTrial(trial);

  //// URL
  const dispatch = useDispatch();
  const { push } = useHistory();
  const isFirstRender = useRef(true);
  const { trialId, date } = match.params;

  // ⚠️ `useEffect` order matters, this effect should be before trials fetch or `isFirstRender` won't be accurate
  useEffect(() => {
    // No action on first render or while trials are being fetched
    if (!isFirstRender.current && !trialsLoading)
      if (trialId) {
        const trial_selected = userTrials.find((t) => t.id === trialId);
        if (trial_selected) {
          const dateToFetch =
            date != null && trial_selected.trial_dates.includes(date)
              ? date
              : trial_selected.trial_dates[
                  trial_selected.trial_dates.length - 1
                ];
          dispatch(fetchResultMapFeatures(trial_selected, dateToFetch));
        } else {
          push("/map");
        }
      } else {
        push("/map");
      }
  }, [
    contractsLoading,
    date,
    dispatch,
    push,
    trialId,
    trialsLoading,
    userTrials,
  ]);

  // ⚠️ `useEffect` order matters, this effect should be after features fetch effect
  useEffect(() => {
    // user's contracts fetched on user login/refresh
    dispatch(fetchUserTrials());
    // Ideally, user's trials should be undefined on first render
    // This trick is used to prevent re-routing on previous effect on first render
    isFirstRender.current = false;
  }, [dispatch]);

  // URL Rewriting whenever trial or date changes
  useEffect(() => {
    if (trial && !refreshing)
      window.history.replaceState({}, "", `/map/${trial.id}/${trialDate}`);
  }, [refreshing, trial, trialDate]);

  return (
    <>
      <Container fluid className="g-0 overflow-hidden worldmap-shell">
        <Col className="position-relative d-flex">
          <WorldMapHud
            mapRef={mapRef}
            selectedTileLayer={selectedTileLayer}
            setSelectedTileLayer={setSelectedTileLayer}
            isOffcanvasOpen={isOffcanvasOpen}
            toggleOffcanvas={toggleOffcanvas}
          />
          <WorldMap
            mapMode={mapMode}
            mapRef={mapRef}
            selectedTileLayer={selectedTileLayer}
          />
        </Col>
      </Container>
      {/* Fixed to the viewport, so outside the HUD sheet and its
          pointer-events: none */}
      <SiteDrawer defaultOpen={!trialId} />
      {/* Fixed to the bottom-left corner, so it can sit over the bottom bar */}
      <MapModeSelector />
      <BottomBar toggleOffcanvas={toggleOffcanvas} />
    </>
  );
};

WorldMapLayout.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      trialId: PropTypes.string,
      date: PropTypes.string,
    }).isRequired,
  }).isRequired,
};
